import { useCallback, useEffect, useState } from 'react';
import { socialApi } from '@/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { FileInput } from '@/components/ui/FileInput';
import { Input } from '@/components/ui/Input';
import { TooltipText } from '@/components/ui/Tooltip';
import { TablePagination } from '@/components/ui/TablePagination';
import { usePagination } from '@/hooks/usePagination';
import type { SocialMediaAsset, SocialMediaFolder } from '@/types/social';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

export function SocialMediaLibraryPage() {
  const pagination = usePagination(24);
  const [folders, setFolders] = useState<SocialMediaFolder[]>([]);
  const [assets, setAssets] = useState<SocialMediaAsset[]>([]);
  const [selectedFolderId, setSelectedFolderId] = useState<number | null>(null);
  const [newFolderName, setNewFolderName] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [folderList, assetPage] = await Promise.all([
        socialApi.listMediaFolders(),
        socialApi.listMediaAssets({
          page: pagination.page,
          size: pagination.size,
          folderId: selectedFolderId ?? undefined,
        }),
      ]);
      setFolders(folderList);
      let rows = assetPage.content ?? [];
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        rows = rows.filter(
          (a) =>
            a.fileName.toLowerCase().includes(q) ||
            (a.tags ?? '').toLowerCase().includes(q),
        );
      }
      setAssets(rows);
      pagination.applyPageResponse(assetPage);
    } catch (err) {
      showApiError(err, 'Failed to load media library');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.size, selectedFolderId, search, pagination]);

  useEffect(() => {
    void load();
  }, [load]);

  const createFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      await socialApi.createMediaFolder(newFolderName.trim());
      setNewFolderName('');
      notify.success('Folder created');
      void load();
    } catch (err) {
      showApiError(err, 'Failed to create folder');
    }
  };

  const onUpload = async (files: File[]) => {
    if (!files.length) return;
    try {
      for (const file of files) {
        await socialApi.uploadMediaAsset(file, selectedFolderId ?? undefined);
      }
      notify.success('Upload complete');
      void load();
    } catch (err) {
      showApiError(err, 'Upload failed');
    }
  };

  const removeAsset = async (id: number) => {
    try {
      await socialApi.deleteMediaAsset(id);
      notify.success('Asset removed');
      void load();
    } catch (err) {
      showApiError(err, 'Delete failed');
    }
  };

  return (
    <div>
      <PageHeader
        title="Media Library"
        description="Images, videos, and campaign assets — organized in folders with search and reuse in Publishing"
        breadcrumbs={[{ label: 'Social', to: '/social' }, { label: 'Media Library' }]}
        actions={
          <FileInput
            variant="button"
            buttonLabel="Upload assets"
            multiple
            accept="image/*,video/*,application/pdf"
            onChange={(files) => void onUpload(files)}
          />
        }
      />

      <div className="grid gap-4 lg:grid-cols-[220px_1fr]">
        <aside className="card p-3 space-y-2">
          <p className="text-xs font-semibold uppercase text-slate-500">Folders</p>
          <button
            type="button"
            className={`w-full rounded px-2 py-1.5 text-left text-sm ${selectedFolderId === null ? 'bg-primary-50 font-medium text-primary-700 dark:bg-primary-900/30' : 'hover:bg-slate-50 dark:hover:bg-slate-800'}`}
            onClick={() => setSelectedFolderId(null)}
          >
            All assets
          </button>
          {folders.map((f) => (
            <button
              key={f.id}
              type="button"
              className={`w-full rounded px-2 py-1.5 text-left text-sm ${selectedFolderId === f.id ? 'bg-primary-50 font-medium text-primary-700 dark:bg-primary-900/30' : 'hover:bg-slate-50 dark:hover:bg-slate-800'}`}
              onClick={() => setSelectedFolderId(f.id)}
            >
              {f.name} ({f.assetCount})
            </button>
          ))}
          <div className="flex gap-1 pt-2">
            <Input
              className="min-w-0 flex-1"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder="New folder"
            />
            <Button size="sm" onClick={() => void createFolder()}>
              +
            </Button>
          </div>
        </aside>

        <div>
          <div className="mb-4 flex flex-wrap gap-2">
            <Input
              className="max-w-xs"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or tag…"
            />
          </div>

          {loading && <p className="text-sm text-slate-500">Loading…</p>}
          {!loading && assets.length === 0 && (
            <div className="card p-8 text-center text-sm text-slate-500">
              No assets yet. Upload images or videos to use in the Publishing Center.
            </div>
          )}
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {assets.map((a) => (
              <article key={a.id} className="card overflow-hidden">
                {a.mimeType?.startsWith('image/') ? (
                  <img src={a.fileUrl} alt={a.fileName} className="h-36 w-full object-cover" />
                ) : (
                  <div className="flex h-36 items-center justify-center bg-slate-100 text-xs text-slate-500 dark:bg-slate-800">
                    {a.mimeType?.startsWith('video/') ? 'Video' : 'Document'}
                  </div>
                )}
                <div className="p-3">
                  <TooltipText text={a.fileName} className="text-sm font-medium" as="p" />
                  {a.tags && <p className="text-xs text-slate-500">{a.tags}</p>}
                  <div className="mt-2 flex gap-2">
                    <a
                      href={a.fileUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs text-primary-600 hover:underline"
                    >
                      Open
                    </a>
                    <button
                      type="button"
                      className="text-xs text-red-600 hover:underline"
                      onClick={() => void removeAsset(a.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
          <TablePagination
            page={pagination.page}
            totalPages={pagination.totalPages}
            totalElements={pagination.totalElements}
            onPageChange={pagination.setPage}
            loading={loading}
            hasRows={assets.length > 0}
          />
        </div>
      </div>
    </div>
  );
}
